<template>
  <div class="admin-view library-view">
    <div class="view-header">
      <div>
        <h2>Library</h2>
        <p class="lib-sub">Everyone and everything you've worked with — categories, locations, models and crew</p>
      </div>
      <button class="btn btn-primary" @click="startNew">+ New {{ singular }}</button>
    </div>

    <!-- Tabs -->
    <div class="lib-tabs">
      <button class="btn btn-sm" :class="tab === 'categories' ? 'btn-primary' : 'btn-outline'" @click="tab = 'categories'">Categories</button>
      <button class="btn btn-sm" :class="tab === 'locations'  ? 'btn-primary' : 'btn-outline'" @click="tab = 'locations'">Locations</button>
      <button class="btn btn-sm" :class="tab === 'people'     ? 'btn-primary' : 'btn-outline'" @click="tab = 'people'">People</button>
    </div>

    <!-- ───────────────────────── CATEGORIES ───────────────────────── -->
    <section v-if="tab === 'categories'">
      <div class="lib-toolbar">
        <input v-model="catQuery" class="filter-select lib-search" placeholder="Search categories…" />
        <span class="muted">{{ filteredCategories.length }} of {{ categories.length }}</span>
      </div>
      <div v-if="!categories.length" class="empty">No categories yet.</div>
      <div v-else class="cat-grid">
        <div v-for="c in filteredCategories" :key="c.id" class="cat-chip" :class="{ 'cat-sub': c.parent_id != null }" @click="editCategory(c)">
          <span class="cat-label">
            <span v-if="c.parent_label" class="cat-parent">{{ c.parent_label }} ›</span>
            {{ c.label }}
          </span>
          <span class="cat-count">{{ c.shoot_count ?? 0 }}</span>
          <button class="row-del" title="Delete" @click.stop="deleteCategory(c)">✕</button>
        </div>
      </div>
    </section>

    <!-- ───────────────────────── LOCATIONS ───────────────────────── -->
    <section v-else-if="tab === 'locations'">
      <div class="lib-toolbar">
        <div class="loc-search-group">
          <input v-model="locQuery" class="filter-select lib-search loc-search" placeholder="Search venue, address, city or state…" @input="debouncedLoadLocations" />
          <label class="inline-check loc-closed-filter">
            <input type="checkbox" v-model="excludeClosed" />
            Hide closed
          </label>
        </div>
        <span class="muted">{{ visibleLocations.length }}<template v-if="excludeClosed && locations.length !== visibleLocations.length"> of {{ locations.length }}</template> total</span>
      </div>
      <div v-if="!visibleLocations.length" class="empty">No locations yet. Add one with “+ New location”.</div>
      <div v-else class="rows">
        <div v-for="l in visibleLocations" :key="l.id" class="row" @click="editLocation(l)">
          <span class="row-main">{{ l.location_venue || l.location_city || '—' }}<span v-if="l.is_closed" class="closed-tag">Closed</span></span>
          <span class="row-sub">{{ locationLine(l) }}</span>
          <span class="row-socials" @click.stop>
            <button v-if="l.photo_count" type="button" class="row-social loc-gallery-btn" :title="l.photo_count + ' photo' + (l.photo_count === 1 ? '' : 's')" @click="openLocationGallery(l)">
              <svg viewBox="0 0 24 24" class="brand-svg" aria-hidden="true"><path fill="currentColor" d="M4 6h12v10H4V6zm2 2v6h8V8H6zm12-2h2v12H8v-2h10V6z"/></svg>
              <span class="row-social-count">{{ l.photo_count }}</span>
            </button>
            <span v-else class="loc-no-photos" title="No photos">
              <svg viewBox="0 0 24 24" class="brand-svg" aria-hidden="true"><path fill="currentColor" d="M4 6h12v10H4V6zm2 2v6h8V8H6zm12-2h2v12H8v-2h10V6z"/></svg>
            </span>
            <SocialLinks :socials="l.socials" />
          </span>
          <span class="row-meta">{{ l.shoot_count }}<template v-if="l.last_shoot"> · {{ fmtDate(l.last_shoot) }}</template></span>
          <button class="row-del" title="Delete" @click.stop="deleteLocation(l)">✕</button>
        </div>
      </div>
    </section>

    <!-- ───────────────────────── PEOPLE ───────────────────────── -->
    <section v-else>
      <div class="lib-toolbar">
        <div class="role-filter">
          <button v-for="r in ROLE_FILTERS" :key="r.value"
                  class="btn btn-sm" :class="roleFilter === r.value ? 'btn-primary' : 'btn-outline'"
                  @click="setRole(r.value)">{{ r.label }}</button>
        </div>
        <input v-model="peopleQuery" class="filter-select lib-search" placeholder="Search name, city or state…" @input="debouncedLoadPeople" />
        <span class="muted">{{ people.length }} total</span>
      </div>
      <div v-if="!people.length" class="empty">No people match this filter yet.</div>
      <div v-else class="rows">
        <div v-for="p in people" :key="p.id" class="row" @click="editPerson(p)">
          <span class="row-avatar" v-if="p.photo_s3_key"><img :src="photoUrl(p.photo_s3_key)" :alt="p.name" loading="lazy" /></span>
          <span class="row-main">{{ p.name }}</span>
          <span class="row-sub">{{ [p.business_name, cityState(p)].filter(Boolean).join(' · ') }}</span>
          <span class="row-socials" @click.stop>
            <SocialLinks :socials="p.socials" />
          </span>
          <span class="row-roles">
            <span v-for="role in (p.roles || [])" :key="role" class="status-badge status-new">{{ roleLabel(role) }}</span>
          </span>
          <span class="row-meta">{{ p.shoot_count ?? 0 }}<template v-if="p.last_worked"> · {{ fmtDate(p.last_worked) }}</template></span>
          <button class="row-del" title="Delete" @click.stop="deletePerson(p)">✕</button>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════ CATEGORY MODAL ═══════════════════════ -->
    <div v-if="catEdit.active" class="modal" @click.self="catEdit.active = false">
      <div class="modal-content">
        <h3>{{ catEdit.id ? 'Edit Category' : 'New Category' }}</h3>
        <form @submit.prevent="saveCategory" novalidate>
          <div class="form-group">
            <label>Name</label>
            <input v-model="catEdit.label" placeholder="e.g. Boudoir" />
          </div>
          <div class="form-group">
            <label>Slug <span class="optional">(optional — auto from name)</span></label>
            <input v-model="catEdit.slug" placeholder="auto" />
          </div>
          <div class="form-group">
            <label>Parent category <span class="optional">(optional — makes this a sub-category)</span></label>
            <select v-model="catEdit.parentId">
              <option :value="null">— None (top-level) —</option>
              <option v-for="c in parentCategoryOptions" :key="c.id" :value="c.id" :disabled="catEdit.id === c.id">{{ c.label }}</option>
            </select>
          </div>
          <p v-if="catEdit.status" class="error" :class="{ ok: !catEdit.err }">{{ catEdit.status }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" @click="catEdit.active = false">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ catEdit.id ? 'Save' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- ═══════════════════════ LOCATION MODAL ═══════════════════════ -->
    <div v-if="locEdit.active" class="modal" @click.self="locEdit.active = false">
      <div class="modal-content">
        <h3>{{ locEdit.id ? 'Edit Location' : 'New Location' }}</h3>
        <form @submit.prevent="saveLocation" novalidate>
          <div class="form-group">
            <label>Venue / place</label>
            <input v-model="locEdit.venue" placeholder="e.g. Hotel Icon" />
          </div>
          <div class="form-group">
            <label>Address</label>
            <input v-model="locEdit.address" placeholder="e.g. 220 Main St" />
          </div>
          <div class="cl-grid-2">
            <div class="form-group">
              <label>City</label>
              <input v-model="locEdit.city" placeholder="Houston" />
            </div>
            <div class="form-group">
              <label>State</label>
              <input v-model="locEdit.state" placeholder="TX" />
            </div>
          </div>
          <div class="form-group">
            <label>Notes <span class="optional">(parking, lighting, access, contacts…)</span></label>
            <textarea v-model="locEdit.notes" rows="3" placeholder="Anything worth remembering about this place."></textarea>
          </div>
          <div class="cl-grid-2">
            <div class="form-group">
              <label>Website <span class="optional">(URL)</span></label>
              <input v-model="locEdit.url" placeholder="https://…" />
            </div>
            <div class="form-group">
              <EmailInput v-model="locEdit.email" label="Email" :optional="true" placeholder="info@venue.com" @update:valid="locEmailValid = $event" />
            </div>
          </div>
          <div class="form-group">
            <label class="inline-check">
              <input type="checkbox" v-model="locEdit.isClosed" />
              Permanently closed <span class="optional">(out of business — kept for history)</span>
            </label>
          </div>
          <SocialLinksEditor v-model="locEdit.socials" hint="(Instagram, Facebook…)" />
          <div class="form-group" v-if="locEdit.id">
            <label>Photos <span class="optional">(scouting / reference)</span></label>
            <div class="lib-photo-grid">
              <div v-for="ph in locPhotos" :key="ph.id" class="lib-photo">
                <img :src="photoUrl(ph.s3_key)" :alt="ph.caption || 'location photo'" loading="lazy" />
                <button type="button" class="lib-photo-del" title="Remove photo" @click="deleteLocationPhoto(ph)">✕</button>
              </div>
              <label class="lib-photo-add" :class="{ busy: locPhotoBusy }">
                <input type="file" accept="image/*" multiple hidden :disabled="locPhotoBusy" @change="uploadLocationPhoto($event)" />
                <span v-if="locPhotoBusy" class="spinner-sm"></span>
                <span v-else>+ Add</span>
              </label>
            </div>
          </div>
          <p v-else class="muted sm">Save the location first to add photos.</p>
          <p v-if="locEdit.status" class="error" :class="{ ok: !locEdit.err }">{{ locEdit.status }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" @click="locEdit.active = false">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="!locEmailValid">{{ locEdit.id ? 'Save' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- ═══════════════════ LOCATION GALLERY VIEWER ═══════════════════ -->
    <GalleryViewer v-model:open="galleryModal.active" :title="galleryModal.title" :loading="galleryModal.loading" :photos="galleryModal.photos" />

    <!-- ═══════════════════════ PERSON MODAL ═══════════════════════ -->
    <div v-if="personEdit.active" class="modal" @click.self="personEdit.active = false">
      <div class="modal-content person-modal">
        <h3>{{ personEdit.id ? 'Edit Person' : 'New Person' }}</h3>
        <form @submit.prevent="savePerson" novalidate>
          <!-- Photo — centered at the top, matching the Client modal -->
          <FormSection photo>
            <PhotoUploadField v-model="personEdit.photoS3Key" kind="person" :ref-id="personEdit.id || 0" :paste-active="personEdit.active" round />
          </FormSection>

          <!-- Name / Business -->
          <FormSection>
            <div class="cl-grid-2">
              <div class="form-group">
                <label>Full name</label>
                <input v-model="personEdit.name" placeholder="Full name" />
              </div>
              <div class="form-group">
                <label>Business <span class="optional">(optional)</span></label>
                <input v-model="personEdit.businessName" placeholder="Business" />
              </div>
            </div>
          </FormSection>

          <!-- Classify as (roles) -->
          <FormSection>
            <div class="form-group">
              <label>Classify as <span class="optional">(what they do — pick any)</span></label>
              <div class="role-picker">
                <button v-for="r in PRIMARY_ROLES" :key="r.value" type="button"
                        class="role-opt" :class="{ on: personEdit.primaryRoles.includes(r.value) }"
                        @click="toggleRole(r.value)">{{ r.label }}</button>
              </div>
            </div>
          </FormSection>

          <!-- City / State -->
          <FormSection>
            <div class="cl-grid-2">
              <div class="form-group">
                <label>City <span class="optional">(optional)</span></label>
                <input v-model="personEdit.city" placeholder="Houston" />
              </div>
              <div class="form-group">
                <label>State <span class="optional">(optional)</span></label>
                <input v-model="personEdit.state" placeholder="TX" />
              </div>
            </div>
          </FormSection>

          <!-- Contact -->
          <FormSection title="Contact">
            <div class="cl-grid-2">
              <div class="form-group">
                <EmailInput v-model="personEdit.email" label="Email" :optional="true" @update:valid="personEmailValid = $event" />
              </div>
              <PhoneInput v-model="personEdit.phone" label="Phone" :optional="true" placeholder="(555) 123-4567" @update:valid="personPhoneValid = $event" />
            </div>
          </FormSection>

          <!-- Birthdate — full-width section (no longer crammed into a grid cell) -->
          <FormSection>
            <div class="form-group">
              <label>Birthdate <span class="optional">(month &amp; day; year optional)</span></label>
              <BirthdatePicker
                v-model:month="personEdit.birthMonth"
                v-model:day="personEdit.birthDay"
                v-model:year="personEdit.birthYear"
              />
            </div>
          </FormSection>

          <!-- Notes -->
          <FormSection>
            <div class="form-group">
              <label>Notes <span class="optional">(optional)</span></label>
              <textarea v-model="personEdit.notes" rows="2"></textarea>
            </div>
          </FormSection>

          <!-- Social media -->
          <FormSection>
            <SocialLinksEditor v-model="personEdit.socials" label="Social media" />
          </FormSection>

          <p v-if="personEdit.status" class="error" :class="{ ok: !personEdit.err }">{{ personEdit.status }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" @click="personEdit.active = false">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="!personPhoneValid || !personEmailValid">{{ personEdit.id ? 'Save' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useAdminApi, photoUrl, jsonOf } from '@/composables/useAdminApi'
import { uploadLibraryPhoto } from '@/lib/uploads'
import SocialLinksEditor from '@/components/SocialLinksEditor.vue'
import SocialLinks from '@/components/SocialLinks.vue'
import PhotoUploadField from '@/components/PhotoUploadField.vue'
import GalleryViewer from '@/components/GalleryViewer.vue'
import PhoneInput from '@/components/PhoneInput.vue'
import EmailInput from '@/components/EmailInput.vue'
import FormSection from '@/components/FormSection.vue'
import BirthdatePicker from '@/components/BirthdatePicker.vue'

const { apiFetch, formatDate } = useAdminApi()
const fmtDate = (d: string) => { try { return formatDate(d) } catch { return d } }

const tab = ref<'categories' | 'locations' | 'people'>('categories')
const singular = computed(() => tab.value === 'categories' ? 'category' : tab.value === 'locations' ? 'location' : 'person')
function startNew() {
  if (tab.value === 'categories') startNewCategory()
  else if (tab.value === 'locations') startNewLocation()
  else startNewPerson()
}

// ─────────────────────────── Categories ───────────────────────────
const categories = ref<any[]>([])
const catQuery = ref('')
const filteredCategories = computed(() => {
  const q = catQuery.value.trim().toLowerCase()
  const list = q
    ? categories.value.filter(c =>
        (c.label || '').toLowerCase().includes(q) || (c.slug || '').toLowerCase().includes(q))
    : categories.value.slice()
  return list.sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }))
})
const catEdit = reactive<{ active: boolean; id: number | null; label: string; slug: string; parentId: number | null; status: string; err: boolean }>(
  { active: false, id: null, label: '', slug: '', parentId: null, status: '', err: false })

// Top-level categories only — the valid parents for a sub-category.
const topLevelCategories = computed(() => categories.value.filter((c: any) => c.parent_id == null))
// Parent-category dropdown: top-level categories, A→Z, shown after the "— None —" entry.
const parentCategoryOptions = computed(() =>
  [...topLevelCategories.value].sort((a: any, b: any) => String(a.label).localeCompare(String(b.label))))

async function loadCategories() {
  try {
    const res = await apiFetch('/admin/portfolio/categories')
    categories.value = (await jsonOf(res)).categories || []
  } catch { categories.value = [] }
}
function startNewCategory() { Object.assign(catEdit, { active: true, id: null, label: '', slug: '', parentId: null, status: '', err: false }) }
function editCategory(c: any) { Object.assign(catEdit, { active: true, id: c.id, label: c.label, slug: c.slug, parentId: c.parent_id ?? null, status: '', err: false }) }
async function saveCategory() {
  const label = catEdit.label.trim()
  if (!label) { catEdit.status = 'Name is required'; catEdit.err = true; return }
  catEdit.status = 'Saving…'; catEdit.err = false
  const method = catEdit.id ? 'PUT' : 'POST'
  const body: any = { label, parentId: catEdit.parentId }
  if (catEdit.id) body.id = catEdit.id
  if (catEdit.slug.trim()) body.slug = catEdit.slug.trim()
  const res = await apiFetch('/admin/portfolio/categories', { method, body: JSON.stringify(body) })
  if (res.ok) { catEdit.active = false; await loadCategories() }
  else { catEdit.status = (await jsonOf(res)).error || 'Could not save'; catEdit.err = true }
}
async function deleteCategory(c: any) {
  if (!confirm(`Delete category "${c.label}"?`)) return
  const res = await apiFetch(`/admin/portfolio/categories?id=${c.id}`, { method: 'DELETE' })
  if (res.ok) await loadCategories()
  else alert((await jsonOf(res)).error || 'Could not delete category')
}

// ─────────────────────────── Locations ───────────────────────────
const locations = ref<any[]>([])
const locQuery = ref('')
// Hide permanently-closed locations by default; the box reveals them.
const excludeClosed = ref(true)
const visibleLocations = computed(() =>
  excludeClosed.value ? locations.value.filter((l: any) => !l.is_closed) : locations.value)
const locEdit = reactive<{ active: boolean; id: number | null; venue: string; address: string; city: string; state: string; notes: string; url: string; email: string; socials: Array<{ platform: string; url: string }>; isClosed: boolean; status: string; err: boolean }>(
  { active: false, id: null, venue: '', address: '', city: '', state: '', notes: '', url: '', email: '', socials: [], isClosed: false, status: '', err: false })

// EmailInput reports validity; blank optional email is valid, a bad one blocks save.
const locEmailValid = ref(true)

// Location photos (loaded when editing an existing location).
const locPhotos = ref<any[]>([])
const locPhotoBusy = ref(false)

// photoUrl + uploadLibraryPhoto are shared (composables/useAdminApi, lib/uploads).
// uploadLibraryPhoto takes apiFetch as its first arg.
const uploadPhoto = (file: File, kind: 'person' | 'location', refId: number) => uploadLibraryPhoto(apiFetch, file, kind, refId)

function locationLine(l: any): string {
  return [l.location_venue, l.location_address, l.location_city, l.location_state].filter(Boolean).join(' · ')
}
async function loadLocations() {
  try {
    const q = locQuery.value.trim()
    const res = await apiFetch(`/admin/portfolio/locations${q ? `?q=${encodeURIComponent(q)}` : ''}`)
    locations.value = (await jsonOf(res)).locations || []
  } catch { locations.value = [] }
}
function startNewLocation() { Object.assign(locEdit, { active: true, id: null, venue: '', address: '', city: '', state: '', notes: '', url: '', email: '', socials: [], isClosed: false, status: '', err: false }); locPhotos.value = [] }
function editLocation(l: any) {
  Object.assign(locEdit, {
    active: true, id: l.id,
    venue: l.location_venue || '', address: l.location_address || '',
    city: l.location_city || '', state: l.location_state || '', notes: l.notes || '',
    url: l.url || '', email: l.email || '',
    socials: Array.isArray(l.socials) ? l.socials.map((s: any) => ({ platform: s.platform, url: s.url })) : [],
    isClosed: !!l.is_closed, status: '', err: false,
  })
  locPhotos.value = []
  loadLocationPhotos(l.id)
}
async function loadLocationPhotos(id: number) {
  try {
    const res = await apiFetch(`/admin/portfolio/locations?photosFor=${id}`)
    locPhotos.value = (await jsonOf(res)).photos || []
  } catch { locPhotos.value = [] }
}

// Read-only photo viewer launched from a location row's gallery icon.
const galleryModal = reactive<{ active: boolean; loading: boolean; title: string; photos: any[] }>(
  { active: false, loading: false, title: '', photos: [] })
async function openLocationGallery(l: any) {
  galleryModal.active = true
  galleryModal.loading = true
  galleryModal.title = l.location_venue || l.location_city || 'Location photos'
  galleryModal.photos = []
  try {
    const res = await apiFetch(`/admin/portfolio/locations?photosFor=${l.id}`)
    galleryModal.photos = (await jsonOf(res)).photos || []
  } catch { galleryModal.photos = [] }
  finally { galleryModal.loading = false }
}
async function uploadLocationPhoto(e: Event) {
  const input = e.target as HTMLInputElement
  const files = Array.from(input.files || [])
  input.value = ''
  if (!files.length || !locEdit.id) return
  locPhotoBusy.value = true
  let failed = 0
  try {
    // Upload each picked file sequentially, appending to the location's photos.
    for (const file of files) {
      const key = await uploadPhoto(file, 'location', locEdit.id)
      if (!key) { failed++; continue }
      const res = await apiFetch('/admin/portfolio/locations', { method: 'POST', body: JSON.stringify({ photoFor: locEdit.id, s3Key: key }) })
      if (!res.ok) { failed++; console.error('attach location photo failed', await jsonOf(res).catch(() => ({}))) }
    }
    await loadLocationPhotos(locEdit.id)
    if (failed) alert(`${failed} of ${files.length} photo${files.length === 1 ? '' : 's'} could not be added.`)
  } finally { locPhotoBusy.value = false }
}
async function deleteLocationPhoto(ph: any) {
  const res = await apiFetch(`/admin/portfolio/locations?photoId=${ph.id}`, { method: 'DELETE' })
  if (res.ok) locPhotos.value = locPhotos.value.filter(p => p.id !== ph.id)
  else alert((await jsonOf(res)).error || 'Could not delete photo')
}
async function saveLocation() {
  if (!locEdit.venue.trim() && !locEdit.address.trim() && !locEdit.city.trim() && !locEdit.state.trim()) {
    locEdit.status = 'Fill in at least one field'; locEdit.err = true; return
  }
  if (!locEmailValid.value) { locEdit.status = 'Enter a valid email address'; locEdit.err = true; return }
  locEdit.status = 'Saving…'; locEdit.err = false
  const method = locEdit.id ? 'PUT' : 'POST'
  const body: any = {
    venue: locEdit.venue.trim(), address: locEdit.address.trim(), city: locEdit.city.trim(), state: locEdit.state.trim(),
    notes: locEdit.notes.trim() || null,
    url: locEdit.url.trim() || null, email: locEdit.email.trim() || null,
    socials: locEdit.socials.filter(s => s.platform && s.url.trim()).map(s => ({ platform: s.platform, url: s.url.trim() })),
    isClosed: locEdit.isClosed,
  }
  if (locEdit.id) body.id = locEdit.id
  const res = await apiFetch('/admin/portfolio/locations', { method, body: JSON.stringify(body) })
  if (res.ok) {
    const data = await jsonOf(res)
    // On first save, keep the modal open on the now-persisted location so the
    // user can immediately add photos (which need the id).
    if (!locEdit.id && data.id) { locEdit.id = data.id; locEdit.status = 'Saved ✓ — you can add photos now'; await loadLocations() }
    else { locEdit.active = false; await loadLocations() }
  }
  else { locEdit.status = (await jsonOf(res)).error || 'Could not save'; locEdit.err = true }
}
async function deleteLocation(l: any) {
  const label = l.location_venue || l.location_city || 'this location'
  if (!confirm(`Delete ${label}?`)) return
  const res = await apiFetch(`/admin/portfolio/locations?id=${l.id}`, { method: 'DELETE' })
  if (res.ok) await loadLocations()
  else alert((await jsonOf(res)).error || 'Could not delete location')
}

// ─────────────────────────── People / contributors ───────────────────────────
const ROLE_FILTERS = [
  { value: '',                   label: 'All' },
  { value: 'model',              label: 'Models' },
  { value: 'hair,hair_makeup',   label: 'Hair' },
  { value: 'makeup,hair_makeup', label: 'Makeup' },
]
const ROLE_LABELS: Record<string, string> = {
  model: 'Model', hair: 'Hair', makeup: 'Makeup', hair_makeup: 'Hair & Makeup',
  stylist: 'Stylist', assistant: 'Assistant', photographer: 'Photographer',
}
function roleLabel(role: string): string { return ROLE_LABELS[role] || role }

// Roles a person can be CLASSIFIED as (independent of per-shoot credits).
const PRIMARY_ROLES = [
  { value: 'model',       label: 'Model' },
  { value: 'hair',        label: 'Hair' },
  { value: 'makeup',      label: 'Makeup' },
  { value: 'hair_makeup', label: 'Hair & Makeup' },
  { value: 'stylist',     label: 'Stylist' },
  { value: 'assistant',   label: 'Assistant' },
  { value: 'photographer',label: 'Photographer' },
]
function cityState(p: any): string { return [p.city, p.state].filter(Boolean).join(', ') }

const people = ref<any[]>([])
const peopleQuery = ref('')
const roleFilter = ref('')
const personEdit = reactive<{ active: boolean; id: number | null; name: string; businessName: string; notes: string; primaryRoles: string[]; city: string; state: string; email: string; phone: string; birthMonth: number | null; birthDay: number | null; birthYear: number | null; photoS3Key: string | null; socials: Array<{ platform: string; url: string }>; status: string; err: boolean }>(
  { active: false, id: null, name: '', businessName: '', notes: '', primaryRoles: [], city: '', state: '', email: '', phone: '', birthMonth: null, birthDay: null, birthYear: null, photoS3Key: null, socials: [], status: '', err: false })
// PhoneInput reports validity; blank optional phone is valid, a bad one blocks save.
const personPhoneValid = ref(true)
// EmailInput reports validity; blank optional email is valid, a bad one blocks save.
const personEmailValid = ref(true)
function toggleRole(r: string) {
  const i = personEdit.primaryRoles.indexOf(r)
  if (i >= 0) personEdit.primaryRoles.splice(i, 1); else personEdit.primaryRoles.push(r)
}

async function loadPeople() {
  try {
    const params = new URLSearchParams()
    if (peopleQuery.value.trim()) params.set('q', peopleQuery.value.trim())
    if (roleFilter.value) params.set('role', roleFilter.value)
    const qs = params.toString()
    const res = await apiFetch(`/admin/portfolio/contributors${qs ? `?${qs}` : ''}`)
    people.value = (await jsonOf(res)).contributors || []
  } catch { people.value = [] }
}
function setRole(r: string) { roleFilter.value = r; loadPeople() }

function startNewPerson() { Object.assign(personEdit, { active: true, id: null, name: '', businessName: '', notes: '', primaryRoles: [], city: '', state: '', email: '', phone: '', birthMonth: null, birthDay: null, birthYear: null, photoS3Key: null, socials: [], status: '', err: false }) }
async function editPerson(p: any) {
  // Pull full detail (socials/notes) — the list row only carries aggregates.
  Object.assign(personEdit, { active: true, id: p.id, name: p.name, businessName: p.business_name || '', notes: '', primaryRoles: [...(p.primary_roles || [])], city: p.city || '', state: p.state || '', email: p.email || '', phone: p.phone || '', birthMonth: p.birth_month ?? null, birthDay: p.birth_day ?? null, birthYear: p.birth_year ?? null, photoS3Key: p.photo_s3_key ?? null, socials: [], status: 'Loading…', err: false })
  try {
    const res = await apiFetch(`/admin/portfolio/contributors?id=${p.id}`)
    const detail = (await jsonOf(res)).contributor
    if (detail) {
      personEdit.name = detail.name || p.name
      personEdit.businessName = detail.business_name || ''
      personEdit.notes = detail.notes || ''
      personEdit.primaryRoles = [...(detail.primary_roles || [])]
      personEdit.city = detail.city || ''
      personEdit.state = detail.state || ''
      personEdit.email = detail.email || ''
      personEdit.phone = detail.phone || ''
      personEdit.birthMonth = detail.birth_month ?? null
      personEdit.birthDay = detail.birth_day ?? null
      personEdit.birthYear = detail.birth_year ?? null
      personEdit.photoS3Key = detail.photo_s3_key ?? null
      personEdit.socials = (detail.socials || []).map((s: any) => ({ platform: s.platform, url: s.url }))
    }
    personEdit.status = ''
  } catch { personEdit.status = '' }
}
async function savePerson() {
  const name = personEdit.name.trim()
  if (!name) { personEdit.status = 'Name is required'; personEdit.err = true; return }
  if (!personPhoneValid.value) { personEdit.status = 'Phone number format is invalid'; personEdit.err = true; return }
  if (!personEmailValid.value) { personEdit.status = 'Enter a valid email address'; personEdit.err = true; return }
  // Birthdate: month and day must be set together (year optional).
  const hasM = personEdit.birthMonth != null, hasD = personEdit.birthDay != null
  if (hasM !== hasD) { personEdit.status = 'Birthdate needs both month and day'; personEdit.err = true; return }
  personEdit.status = 'Saving…'; personEdit.err = false
  const body: any = {
    name, businessName: personEdit.businessName.trim() || null, notes: personEdit.notes.trim() || null,
    primaryRoles: personEdit.primaryRoles,
    city: personEdit.city.trim() || null, state: personEdit.state.trim() || null,
    email: personEdit.email.trim() || null, phone: personEdit.phone.trim() || null,
    birthMonth: hasM ? personEdit.birthMonth : null,
    birthDay: hasD ? personEdit.birthDay : null,
    birthYear: personEdit.birthYear || null,
    photoS3Key: personEdit.photoS3Key ?? null,
    socials: personEdit.socials.filter(s => s.platform && s.url.trim()).map(s => ({ platform: s.platform, url: s.url.trim() })),
  }
  if (personEdit.id) body.id = personEdit.id
  const res = await apiFetch('/admin/portfolio/contributors', { method: 'POST', body: JSON.stringify(body) })
  if (res.ok) { personEdit.active = false; await loadPeople() }
  else { personEdit.status = (await jsonOf(res)).error || 'Could not save'; personEdit.err = true }
}
async function deletePerson(p: any) {
  if (!confirm(`Delete ${p.name}?`)) return
  const res = await apiFetch(`/admin/portfolio/contributors?id=${p.id}`, { method: 'DELETE' })
  if (res.ok) await loadPeople()
  else alert((await jsonOf(res)).error || 'Could not delete person')
}

// Lightweight debounce for the search inputs.
let locTimer: any, peopleTimer: any
function debouncedLoadLocations() { clearTimeout(locTimer); locTimer = setTimeout(loadLocations, 220) }
function debouncedLoadPeople() { clearTimeout(peopleTimer); peopleTimer = setTimeout(loadPeople, 220) }

onMounted(() => { loadCategories(); loadLocations(); loadPeople() })
</script>

<style scoped>
.lib-sub { margin: 0.35rem 0 0; color: var(--text-muted); font-size: 0.9rem; }

.lib-tabs { display: flex; gap: 0.5rem; margin-bottom: 1.5rem; }

.lib-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
.lib-search { min-width: 240px; }
/* Locations toolbar: search + Hide closed grouped together (L1) */
.loc-search-group { display: flex; align-items: center; gap: 0.85rem; flex: 1 1 auto; min-width: 0; }
.loc-search { flex: 1 1 auto; min-width: 260px; max-width: 520px; }
.loc-closed-filter { flex: 0 0 auto; white-space: nowrap; }
/* "No photos" indicator — dimmed camera glyph so presence/absence is always visible */
.loc-no-photos {
  display: inline-flex; align-items: center; justify-content: center;
  width: 1.3rem; height: 1.3rem; color: var(--text-muted); opacity: 0.4;
}
.loc-no-photos .brand-svg { width: 0.95rem; height: 0.95rem; }
.role-filter { display: flex; gap: 0.5rem; flex-wrap: wrap; }

/* ── Categories: dense chip grid ── */
.cat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 0.5rem;
}
.cat-chip {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  background: var(--bg-surface); border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm); cursor: pointer; transition: border-color 0.15s, background 0.15s;
}
.cat-chip:hover { background: var(--bg-surface-hover); border-color: var(--border-default); }
.cat-label { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.9rem; }
.cat-count {
  flex: 0 0 auto; font-size: 0.72rem; font-weight: 600;
  background: var(--accent-dim); color: var(--accent-text);
  padding: 0.1rem 0.45rem; border-radius: 999px;
}

/* ── Locations / People: dense single-line rows ── */
.rows { display: flex; flex-direction: column; }
.row {
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer; transition: background 0.12s;
}
.row:hover { background: var(--bg-surface-hover); }
.row-main { flex: 0 0 auto; font-weight: 600; font-size: 0.9rem; max-width: 40%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row-sub  { flex: 1 1 auto; min-width: 0; color: var(--text-secondary); font-size: 0.82rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.row-roles { flex: 0 0 auto; display: flex; gap: 0.25rem; }
.row-socials { flex: 0 0 auto; display: flex; gap: 0.35rem; align-items: center; }
.row-social {
  display: inline-flex; align-items: center; justify-content: center;
  width: 1.3rem; height: 1.3rem; color: var(--text-muted);
  border-radius: var(--radius-sm); transition: color 0.12s, background 0.12s;
}
.row-social:hover { color: var(--accent-text); background: var(--accent-dim); }
.row-social .brand-svg { width: 0.95rem; height: 0.95rem; display: block; }
.row-meta { flex: 0 0 auto; color: var(--text-muted); font-size: 0.78rem; white-space: nowrap; }

/* Delete: ghosted until row hover, so it doesn't clutter the dense list */
.row-del {
  flex: 0 0 auto; width: 1.5rem; height: 1.5rem; line-height: 1;
  background: transparent; border: none; cursor: pointer;
  color: var(--text-muted); font-size: 0.85rem; border-radius: var(--radius-sm);
  opacity: 0; transition: opacity 0.12s, color 0.12s, background 0.12s;
}
.row:hover .row-del, .cat-chip:hover .row-del { opacity: 1; }
.row-del:hover { color: var(--danger-text); background: var(--danger-dim); }
.cat-chip .row-del { opacity: 0; }

/* Two-column form grid (mirrors ClientsView .cl-grid-2) */
.cl-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }

/* People list avatar */
.row-avatar { flex: 0 0 auto; width: 1.9rem; height: 1.9rem; border-radius: 50%; overflow: hidden; background: var(--bg-elevated); margin-right: 0.1rem; }
.row-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Location photo grid */
.lib-photo-grid { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.lib-photo { position: relative; width: 5rem; height: 5rem; border-radius: var(--radius-sm); overflow: hidden; background: var(--bg-elevated); }
.lib-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.lib-photo-del { position: absolute; top: 2px; right: 2px; width: 1.2rem; height: 1.2rem; line-height: 1; border: none; border-radius: 4px; background: rgba(0,0,0,0.6); color: #fff; cursor: pointer; font-size: 0.75rem; }
.lib-photo-del:hover { background: var(--danger-text, #c0392b); }
.lib-photo-add { width: 5rem; height: 5rem; border: 1px dashed var(--border-default, var(--text-muted)); border-radius: var(--radius-sm); display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-muted); font-size: 0.85rem; }
.lib-photo-add:hover { color: var(--accent); border-color: var(--accent); }
.lib-photo-add.busy { opacity: 0.6; cursor: default; }
.muted.sm { font-size: 0.8rem; }
.spinner-sm { width: 0.9rem; height: 0.9rem; border: 2px solid var(--text-muted); border-top-color: transparent; border-radius: 50%; display: inline-block; animation: libspin 0.7s linear infinite; }
@keyframes libspin { to { transform: rotate(360deg); } }
@media (max-width: 520px) { .cl-grid-2 { grid-template-columns: 1fr; } }

.person-modal { max-width: 520px; }

.role-picker { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.role-opt {
  padding: 0.35rem 0.7rem; font-size: 0.82rem; cursor: pointer;
  background: var(--bg-surface); color: var(--text-secondary);
  border: 1px solid var(--border-default); border-radius: 999px; transition: all 0.12s;
}
.role-opt:hover { color: var(--text-primary); border-color: var(--accent); }
.role-opt.on { background: var(--accent-dim); color: var(--accent-text); border-color: var(--accent); font-weight: 600; }

/* brand-svg: used by the location-row gallery icon (social icons live in SocialLinks.vue) */
.brand-svg { width: 1.05rem; height: 1.05rem; display: block; }

/* Row-level responsiveness: drop secondary columns on narrow screens */
@media (max-width: 600px) {
  .row-sub { display: none; }
  .row-main { max-width: 55%; }
}

.error.ok { color: var(--text-muted); }

/* Sub-category chips + parent breadcrumb */
.cat-chip.cat-sub { border-left: 3px solid var(--accent); }
.cat-parent { color: var(--text-muted); font-weight: 400; margin-right: 0.2rem; }

/* Closed-location indicators */
.closed-tag { margin-left: 0.5rem; font-size: 0.65rem; padding: 0.08rem 0.4rem; border-radius: 4px; background: rgba(220,80,80,0.18); color: var(--danger-text, #ff8a8a); text-transform: uppercase; letter-spacing: 0.03em; vertical-align: middle; }

/* Inline checkbox row (e.g. "Permanently closed") */
.inline-check { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
.inline-check input { width: auto; margin: 0; }

/* Locations "hide closed" filter */
.loc-closed-filter { font-size: 0.82rem; color: var(--text-secondary); white-space: nowrap; }

/* Gallery icon + count on a location list row */
.loc-gallery-btn { border: none; background: transparent; cursor: pointer; gap: 0.15rem; padding: 0 0.15rem; width: auto; }
.row-social-count { font-size: 0.68rem; color: var(--text-muted); }
</style>
